import React, { useState, useEffect } from 'react';
import { translations } from '../translations';
import apiService from '../services/apiService';

const WeightMeasurement = ({ patientData, taskId, language = 'en', onComplete, onClose }) => {
  const [measurement, setMeasurement] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const t = translations[language] || translations.en;

  // Charger les réponses existantes si elles existent
  useEffect(() => {
    const loadExistingResponses = async () => {
      if (!patientData || !patientData.patientId || !taskId) return;
      
      try {
        const response = await apiService.getQuestionnaireResponse(patientData.patientId, taskId);
        if (response.success && response.data) {
          const existingResponses = JSON.parse(response.data.responses);
          if (existingResponses.body_weight) {
            setMeasurement(existingResponses.body_weight.toString());
            setIsEditing(true);
          }
        }
      } catch (error) {
        console.error('Erreur chargement réponses existantes:', error);
      }
    };

    loadExistingResponses();
  }, [patientData, taskId]);

  const measurementValue = measurement ? parseFloat(measurement) : 0;
  const isValid = measurement && !isNaN(measurementValue) && measurementValue >= 30 && measurementValue <= 280;

  const handleSubmit = async () => {
    if (!isValid) return;

    setLoading(true);
    setError(null);

    try {
      const measurementValue = parseFloat(measurement);
      
      if (isEditing) {
        // Mode édition : utiliser la route de mise à jour
        await apiService.updateQuestionnaireResponse(
          patientData.patientId,
          taskId,
          'WEIGHT_MEASUREMENT',
          JSON.stringify({ body_weight: measurementValue }),
          measurementValue
        );
      } else {
        // Mode création : utiliser la route de soumission
        const responseData = {
          patientId: patientData.patientId,
          taskId: taskId,
          questionnaireType: 'WEIGHT_MEASUREMENT',
          responses: JSON.stringify({ body_weight: measurementValue }),
          score: measurementValue,
          language: language,
          submissionDate: (() => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
          })()
        };
        await apiService.submitQuestionnaire(responseData);
      }
      
      if (onComplete) {
        onComplete(measurementValue);
      }
      try {
        window.dispatchEvent(new CustomEvent('measurements_updated', { detail: { type: 'weight' } }));
      } catch (_) {}
    } catch (error) {
      console.error('Erreur soumission mesure:', error);
      setError(language === 'de' 
        ? 'Fehler beim Speichern der Messung'
        : 'Error saving measurement'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content weight-measurement-modal">
        <div className="questionnaire-header">
          <h2 className="questionnaire-title">
            {language === 'de' 
              ? `Körpergewicht Messung ${isEditing ? '(Bearbeiten)' : ''}` 
              : `Body Weight Measurement ${isEditing ? '(Edit)' : ''}`
            }
          </h2>
          <button className="questionnaire-close" onClick={onClose}>×</button>
        </div>

        <div className="weight-measurement-content">
          {/* Section d'image et instructions */}
          <div className="weight-instructions-section">
            <h3 className="instructions-title">
              {language === 'de' 
                ? 'Wichtige Hinweise für eine genaue Messung' 
                : 'Important notes for accurate measurement'
              }
            </h3>
            
            <div className="tutorial-image-container">
              <img 
                src="/images/weight-image.jpg" 
                alt={language === 'de' ? 'Körpergewicht Messung Anleitung' : 'Body Weight Measurement Guide'}
                className="tutorial-image"
              />
            </div>
            
            <div className="measurement-tips">
              <h4>
                {language === 'de' ? 'Tipps für die Messung:' : 'Measurement tips:'}
              </h4>
              <ul>
                <li>
                  {language === 'de' 
                    ? 'Idealerweise messen Sie morgens nach dem Aufstehen'
                    : 'Ideally measure in the morning after waking up'
                  }
                </li>
                <li>
                  {language === 'de' 
                    ? 'Messen Sie auf nüchternen Magen (vor dem Frühstück)'
                    : 'Measure on an empty stomach (before breakfast)'
                  }
                </li>
                <li>
                  {language === 'de' 
                    ? 'Verwenden Sie die gleiche Waage wie gewöhnlich'
                    : 'Use the same scale as usual'
                  }
                </li>
                <li>
                  {language === 'de' 
                    ? 'Tragen Sie leichte Kleidung oder messen Sie nackt'
                    : 'Wear light clothing or measure naked'
                  }
                </li>
              </ul>
            </div>
            
            <div className="hydration-reminder">
              <span className="water-drop">💧</span>
              <span className="hydration-text">
                {language === 'de' 
                  ? 'Denken Sie daran, ausreichend hydriert zu bleiben!'
                  : 'Remember to stay well hydrated!'
                }
              </span>
            </div>
          </div>

          {/* Section de saisie */}
          <div className="measurement-input-section">
            <div className="input-group">
              <label htmlFor="weight-measurement" className="input-label">
                {language === 'de' 
                  ? 'Körpergewicht (kg):' 
                  : 'Body weight (kg):'
                }
              </label>
              <div className="input-container">
                <input
                  id="weight-measurement"
                  type="number"
                  value={measurement}
                  onChange={(e) => setMeasurement(e.target.value)}
                  placeholder={language === 'de' ? 'z.B. 70.5' : 'e.g. 70.5'}
                  className="measurement-input"
                  min="30"
                  max="280"
                  step="0.1"
                />
                <span className="input-unit">kg</span>
              </div>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="modal-actions">
              <button 
                onClick={handleSubmit}
                className="btn btn-primary"
                disabled={!isValid || loading}
              >
                {loading 
                  ? (language === 'de' ? 'Speichern...' : 'Saving...') 
                  : (language === 'de' ? 'Speichern' : 'Save')
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeightMeasurement; 